import { useEffect, useState } from "react";
import api, { API_BASE, openPdf, formatApiErrorDetail, normalizeDecimal, DECIMAL_HINT, containerToBase, containerBase, containerFactor } from "../lib/api";
import PageHeader from "../components/PageHeader";
import Pagination from "../components/Pagination";
import { TypeBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Search, Pencil, Printer, Trash2, Wand2, Layers, Image as ImageIcon, Upload, FileUp, Copy, Barcode } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../context/ConfirmContext";
import { Link, useNavigate } from "react-router-dom";
import ImportDialog from "../components/ImportDialog";
import ItemCombobox from "../components/ItemCombobox";

const empty = {
  sku: "", name: "", type: "raw", unit: "kg",
  barcode: "", description: "",
  default_shelf_life_days: "", min_stock: 0, max_stock: "", price: "", purchase_price: "",
  vat_rate: 0.07, warehouse_location: "",
  product_group: "",
  image_data: "",
  container_size: "", container_unit: "kg", container_label: "Sack",
  density: "",
  weight_g: "",
  production_cost: "", target_margin_pct: 40,
  bundle_components: [],
  bundles: {
    "13er":    { enabled: false, price: "", ean: "" },
    "40er":    { enabled: false, price: "", ean: "" },
    "palette": { enabled: false, price: "", ean: "" },
  },
};

const EMPTY_BULK = {
  min_stock: "", price: "", vat_rate: "",
  warehouse_location: "", default_shelf_life_days: "", unit: "",
};

const BUNDLE_UNITS = { "13er": 13, "40er": 40, "palette": 520 };
const autoBundlePrice = (p, code) => {
  const v = Number(p);
  return v > 0 ? String(Math.round(v * (BUNDLE_UNITS[code] || 1) * 100) / 100) : "";
};

export default function Items() {
  const [items, setItems] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState(EMPTY_BULK);
  const [bulkSaving, setBulkSaving] = useState(false);
  // Chargen drill-down
  const [batchesFor, setBatchesFor] = useState(null); // {id, name, sku, unit}
  const [batchesRows, setBatchesRows] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const confirm = useConfirm();
  const nav = useNavigate();

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Bild zu groß (max 2 MB)");
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    setForm((f) => ({ ...f, image_data: dataUrl }));
  };

  const load = async () => {
    const params = { page, page_size: pageSize };
    if (typeFilter !== "all") params.type = typeFilter;
    if (q) params.q = q;
    const r = await api.get("/items", { params });
    setItems(r.data);
    setTotal(Number(r.headers["x-total-count"]) || r.data.length);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, typeFilter, page]);
  useEffect(() => { setPage(1); }, [q, typeFilter]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/product-groups").then((r) => setProductGroups(r.data)).catch(() => {}); }, []);
  // Reset selection when list changes (page/filter switch)
  useEffect(() => { setSelected(new Set()); }, [page, q, typeFilter, pageSize]);

  const allIds = items.map((i) => i.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = allIds.some((id) => selected.has(id));
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (allSelected) { setSelected(new Set()); return; }
    setSelected(new Set(allIds));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = [...selected];    if (ids.length === 0) return;
    const ok = await confirm({
      title: `${ids.length} Artikel löschen?`,
      description: "Artikel mit Referenzen in Bestellungen oder Lieferscheinen können nicht gelöscht werden. Artikel mit Bestand werden im nächsten Schritt separat bestätigt.",
      confirmLabel: `${ids.length} löschen`,
      cancelLabel: "Abbrechen",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/items/bulk-delete", { ids });
      const skipped = data.skipped || [];
      const stockBlocked = skipped.filter((s) => s.code === "stock");
      const hardBlocked = skipped.filter((s) => s.code !== "stock");

      if (data.deleted > 0) toast.success(`${data.deleted} Artikel gelöscht`);
      hardBlocked.forEach((s) => toast.warning(`${s.sku || s.id.slice(-6)}: ${s.reason}`));

      if (stockBlocked.length > 0) {
        const details = stockBlocked.map((s) => ({
          left: s.sku || s.id.slice(-8),
          right: `Bestand ${s.stock}`,
        }));
        const force = await confirm({
          title: `${stockBlocked.length} Artikel haben noch Bestand`,
          description: "Bestand und zugehörige Chargen werden mit gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.",
          confirmLabel: "Trotzdem löschen",
          cancelLabel: "Abbrechen",
          variant: "danger",
          details,
        });
        if (force) {
          const forceIds = stockBlocked.map((s) => s.id);
          const { data: f } = await api.post("/items/bulk-delete", { ids: forceIds, force: true });
          if (f.deleted > 0) toast.success(`${f.deleted} weitere Artikel (mit Bestand) gelöscht`);
          (f.skipped || []).forEach((s) => toast.error(`${s.sku || s.id.slice(-6)}: ${s.reason}`));
        }
      }

      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk-Löschen fehlgeschlagen");
    }
  };

  const openBulkEdit = () => { setBulk(EMPTY_BULK); setBulkOpen(true); };
  const saveBulkEdit = async (e) => {
    e.preventDefault();
    const ids = [...selected];
    if (ids.length === 0) return;
    const changes = {};
    Object.entries(bulk).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) changes[k] = v; });
    if (Object.keys(changes).length === 0) return toast.error("Keine Änderungen angegeben");
    setBulkSaving(true);
    try {
      const { data } = await api.post("/items/bulk-update", { ids, changes });
      toast.success(`${data.modified} Artikel aktualisiert (${Object.keys(data.applied).join(", ")})`);
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk-Update fehlgeschlagen");
    } finally { setBulkSaving(false); }
  };

  const openCreate = () => { setForm(empty); setEditing(null); setPriceHistory([]); setOpen(true); };
  const openEdit = async (it) => {
    setForm({
      ...empty,
      ...it,
      default_shelf_life_days: it.default_shelf_life_days ?? "",
      price: it.price ?? "",
      purchase_price: it.purchase_price ?? "",
      vat_rate: it.vat_rate ?? 0.07,
      supplier_id: it.supplier_id ?? "",
      bundle_components: (it.bundle_components || []).map((c) => ({
        item_id: c.item_id, quantity: c.quantity ?? 1,
      })),
      bundles: {
        "13er":    { enabled: false, price: "", ean: "", ...(it.bundles?.["13er"]    || {}) },
        "40er":    { enabled: false, price: "", ean: "", ...(it.bundles?.["40er"]    || {}) },
        "palette": { enabled: false, price: "", ean: "", ...(it.bundles?.["palette"] || {}) },
      },
    });
    setEditing(it.id);
    setOpen(true);
    try {
      const { data } = await api.get(`/items/${it.id}/price-history`);
      setPriceHistory(data);
    } catch (_e) { setPriceHistory([]); }
  };

  const save = async (e) => {
    e.preventDefault();
    // Sanitise bundle fields: drop empty ones, coerce numbers
    const bundles = {};
    for (const code of ["13er", "40er", "palette"]) {
      const b = form.bundles?.[code] || {};
      bundles[code] = {
        enabled: !!b.enabled,
        price: b.price === "" ? null : Number(b.price),
        ean: b.ean || null,
      };
    }
    const payload = {
      ...form,
      min_stock: Number(form.min_stock || 0),
      max_stock: form.max_stock === "" || form.max_stock == null ? null : Number(form.max_stock),
      default_shelf_life_days: form.default_shelf_life_days === "" ? null : Number(form.default_shelf_life_days),
      price: form.price === "" ? null : Number(form.price),
      purchase_price: form.purchase_price === "" ? null : Number(form.purchase_price),
      container_size: form.container_size === "" || form.container_size == null ? null : Number(form.container_size),
      density: form.density === "" || form.density == null ? null : Number(form.density),
      weight_g: form.weight_g === "" || form.weight_g == null ? null : Number(form.weight_g),
      bundle_components: form.type === "bundle"
        ? (form.bundle_components || [])
            .filter((c) => c.item_id && Number(c.quantity) > 0)
            .map((c) => ({ item_id: c.item_id, quantity: Number(c.quantity) }))
        : null,
      vat_rate: Number(form.vat_rate),
      supplier_id: form.supplier_id || null,
      bundles,
    };
    try {
      if (editing) await api.patch(`/items/${editing}`, payload);
      else await api.post("/items", payload);
      toast.success(editing ? "Artikel aktualisiert" : "Artikel angelegt");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="page-items">
      <PageHeader
        eyebrow="Stammdaten"
        title="Artikel"
        description="Rohstoffe, Verpackungen und Fertigwaren. SKUs, Barcodes und Mindestbestände."
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="btn-batch-shelf"
              variant="outline"
              onClick={async () => {
                const ids = items.map((i) => i.id);
                if (ids.length === 0) return toast.error("Keine Artikel");
                try {
                  await openPdf("/items/shelf-labels.pdf", { method: "POST", body: ids });
                } catch (_e) { toast.error("Fehler beim Sammel-PDF"); }
              }}
              className="rounded-full"
              title="Alle sichtbaren Artikel als Regal-Etiketten drucken"
            >
              <Printer size={16} className="mr-1" /> Regal-Etiketten (alle)
            </Button>
            <Button variant="outline" className="rounded-full" data-testid="btn-import-items"
                    onClick={() => setImportOpen(true)}>
              <FileUp size={16} className="mr-1" /> Importieren
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="btn-new-item"
                  onClick={openCreate}
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
                >
                  <Plus size={16} className="mr-1" /> Neuer Artikel
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  {editing ? "Artikel bearbeiten" : "Neuer Artikel"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="grid grid-cols-2 gap-x-5 gap-y-5 [&_input]:h-10 [&_button[role=combobox]]:h-10">
                <div className="col-span-2 flex items-center gap-4 pb-3 border-b border-[var(--border)]">
                  <div className="w-24 h-24 rounded-xl border border-[var(--border)] bg-[var(--pop-cream)]/30 flex items-center justify-center overflow-hidden shrink-0">
                    {form.image_data
                      ? <img src={form.image_data} alt="" className="w-full h-full object-cover" />
                      : <ImageIcon size={26} className="text-[var(--text-muted)]" />}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>Artikelbild <span className="text-[10px] text-[var(--text-muted)]">(max 2 MB)</span></Label>
                    <div className="flex items-center gap-2">
                      <input type="file" accept="image/*" id="item-image-input" className="hidden"
                             data-testid="input-item-image-file"
                             onChange={(e) => uploadImage(e.target.files?.[0])} />
                      <Button type="button" variant="outline" size="sm" className="rounded-full"
                              onClick={() => document.getElementById("item-image-input")?.click()}
                              data-testid="btn-item-image-upload">
                        <Upload size={12} className="mr-1" /> {form.image_data ? "Bild ändern" : "Bild hochladen"}
                      </Button>
                      {form.image_data && (
                        <Button type="button" variant="ghost" size="sm" className="rounded-full text-red-700 text-xs"
                                onClick={() => setForm({ ...form, image_data: "" })}
                                data-testid="btn-item-image-remove">Entfernen</Button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>SKU *</Label><Input required data-testid="input-item-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Name *</Label><Input required data-testid="input-item-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="space-y-1.5">
                  <Label>Typ</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="select-item-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="raw">Rohstoff</SelectItem>
                      <SelectItem value="packaging">Verpackung</SelectItem>
                      <SelectItem value="finished">Fertigware</SelectItem>
                      <SelectItem value="bundle">Bundle / Set</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Einheit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="kg / stk / g" /></div>
                <div className="space-y-1.5"><Label>Barcode / EAN</Label>
                  <div className="flex gap-1">
                    <Input value={form.barcode} data-testid="input-item-barcode"
                           onChange={(e) => setForm({ ...form, barcode: e.target.value })} />
                    <Button type="button" size="sm" variant="outline" className="rounded-lg px-3 h-10"
                            data-testid="btn-barcode-lookup"
                            onClick={async () => {
                              if (!form.barcode) return toast.error("Bitte Barcode eintragen");
                              try {
                                const { data } = await api.get(`/barcode-lookup/${form.barcode}`);
                                if (!data.found) return toast.warning("Kein Treffer bei OpenFoodFacts");
                                setForm((f) => ({
                                  ...f,
                                  name: f.name || data.name || f.name,
                                  image_data: data.image_url ? data.image_url : f.image_data,
                                }));
                                toast.success(`Gefunden: ${data.name || "—"} · ${data.brand || ""}`);
                              } catch { toast.error("Barcode-Lookup fehlgeschlagen"); }
                            }}
                            title="Bild + Name aus OpenFoodFacts holen">
                      📷
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Regalplatz</Label><Input data-testid="input-item-location" placeholder="z.B. A-01-03" value={form.warehouse_location || ""} onChange={(e) => setForm({ ...form, warehouse_location: e.target.value })} /></div>
                <div className="space-y-1.5">
                  <Label>Warengruppe
                    <span className="text-[10px] text-[var(--text-muted)] ml-1">(→ Auto-Charge)</span>
                  </Label>
                  <Select value={form.product_group || "none"}
                          onValueChange={(v) => setForm({ ...form, product_group: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="select-item-product-group"><SelectValue placeholder="Keine…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Keine —</SelectItem>
                      {productGroups.map((g) => (
                        <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-[var(--text-muted)]">
                    Gruppen pflegen unter Einstellungen &rarr; Warengruppen.
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Mindestbestand</Label><Input type="number" step="any" placeholder={DECIMAL_HINT} data-testid="input-min-stock" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: normalizeDecimal(e.target.value) })} /></div>
                <div className="space-y-1.5"><Label>Maximalbestand</Label><Input type="number" step="any" placeholder={DECIMAL_HINT} data-testid="input-max-stock" value={form.max_stock} onChange={(e) => setForm({ ...form, max_stock: normalizeDecimal(e.target.value) })} /></div>
                <div className="space-y-1.5"><Label>MHD-Standard (Tage)</Label><Input type="number" value={form.default_shelf_life_days} onChange={(e) => setForm({ ...form, default_shelf_life_days: e.target.value })} /></div>
                {form.type === "raw" && (
                  <div className="col-span-2 rounded-xl bg-neutral-50 border border-neutral-200 p-4 space-y-4" data-testid="section-gebinde">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="text-sm font-bold text-neutral-800">Standard-Gebinde</div>
                      <div className="text-[11px] text-neutral-500">
                        Basiseinheit: <b className="text-neutral-700">{containerBase(form.container_unit)}</b> · Dezimal mit Punkt
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Gebindegröße</Label>
                        <Input type="number" step="any" placeholder="z.B. 8.5"
                                data-testid="input-item-container-size"
                                value={form.container_size || ""}
                                onChange={(e) => setForm({ ...form, container_size: normalizeDecimal(e.target.value) })} />
                        {form.container_size && (
                          <div className="text-[10px] text-neutral-500 font-mono" data-testid="gebinde-conversion-hint">
                            = {containerToBase(form.container_size, form.container_unit).toLocaleString("de-DE")} {containerBase(form.container_unit)}
                          </div>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Einheit</Label>
                        <Select value={form.container_unit || "kg"}
                                 onValueChange={(v) => setForm({ ...form, container_unit: v })}>
                          <SelectTrigger data-testid="select-item-container-unit"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">Kilogramm (kg)</SelectItem>
                            <SelectItem value="g">Gramm (g)</SelectItem>
                            <SelectItem value="l">Liter (l)</SelectItem>
                            <SelectItem value="ml">Milliliter (ml)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Bezeichnung</Label>
                        <Select value={form.container_label || "Sack"}
                                 onValueChange={(v) => setForm({ ...form, container_label: v })}>
                          <SelectTrigger data-testid="select-item-container-label"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Sack">Sack</SelectItem>
                            <SelectItem value="Karton">Karton</SelectItem>
                            <SelectItem value="Eimer">Eimer</SelectItem>
                            <SelectItem value="Fass">Fass</SelectItem>
                            <SelectItem value="Palette">Palette</SelectItem>
                            <SelectItem value="Kiste">Kiste</SelectItem>
                            <SelectItem value="Flasche">Flasche</SelectItem>
                            <SelectItem value="Packung">Packung</SelectItem>
                            <SelectItem value="Kanister">Kanister</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="pt-3 border-t border-neutral-200 space-y-1.5">
                      <Label className="text-xs">Dichte (g/ml) <span className="text-neutral-400 font-normal">— optional</span></Label>
                      <Input type="number" step="any" placeholder="z.B. 0.92"
                             data-testid="input-item-density"
                             value={form.density || ""}
                             onChange={(e) => setForm({ ...form, density: normalizeDecimal(e.target.value) })} />
                      <div className="text-[10px] text-neutral-500 leading-snug">
                        Nur nötig, wenn Rezepturen g↔ml umrechnen sollen.<br />
                        Beispiele: Wasser 1.0 · Milch 1.03 · Öl 0.92 · Honig 1.42.
                      </div>
                    </div>
                  </div>
                )}
                <div className="space-y-1.5"><Label>Preis VK (€)</Label><Input type="number" step="0.01" placeholder="z.B. 2.99" data-testid="input-item-price" value={form.price} onChange={(e) => {
                  const np = normalizeDecimal(e.target.value); const op = form.price;
                  const nb = { ...(form.bundles || {}) };
                  ["13er", "40er", "palette"].forEach((code) => {
                    const bb = nb[code];
                    if (bb && bb.enabled && (bb.price == null || bb.price === "" || String(bb.price) === autoBundlePrice(op, code))) {
                      nb[code] = { ...bb, price: autoBundlePrice(np, code) };
                    }
                  });
                  setForm({ ...form, price: np, bundles: nb });
                }} /></div>
                <div className="space-y-1.5"><Label>Einkaufspreis (€)<span className="text-[10px] text-[var(--text-muted)] ml-1">(Netto)</span></Label><Input type="number" step="0.01" data-testid="input-item-purchase-price" placeholder="z.B. 1.85" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: normalizeDecimal(e.target.value) })} /></div>
                <div className="space-y-1.5"><Label>Gewicht je Einheit (g)<span className="text-[10px] text-[var(--text-muted)] ml-1">(Versand)</span></Label><Input type="number" step="any" data-testid="input-item-weight-g" placeholder="z.B. 120" value={form.weight_g || ""} onChange={(e) => setForm({ ...form, weight_g: normalizeDecimal(e.target.value) })} /></div>
                {form.type === "finished" && (
                  <>
                    <div className="space-y-1.5"><Label>Produktionskosten je Charge (€)<span className="text-[10px] text-[var(--text-muted)] ml-1">(fix)</span></Label>
                      <Input type="number" step="0.01" data-testid="input-item-production-cost"
                              placeholder="z.B. 15.00" value={form.production_cost || ""}
                              onChange={(e) => setForm({ ...form, production_cost: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Ziel-Marge (%)</Label>
                      <Input type="number" step="1" min="0" max="99" data-testid="input-item-target-margin"
                              placeholder="z.B. 40" value={form.target_margin_pct || ""}
                              onChange={(e) => setForm({ ...form, target_margin_pct: e.target.value })} /></div>
                  </>
                )}
                <div className="space-y-1.5">
                  <Label>MwSt-Satz</Label>
                  <Select value={String(form.vat_rate)} onValueChange={(v) => setForm({ ...form, vat_rate: Number(v) })}>
                    <SelectTrigger data-testid="select-item-vat"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.07">7 % (Lebensmittel)</SelectItem>
                      <SelectItem value="0.19">19 % (Standard)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Lieferant (optional)</Label>
                  <Select value={form.supplier_id || "none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein Lieferant</SelectItem>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5"><Label>Beschreibung</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

                {/* Bundle-Komponenten (nur bei type=bundle) */}
                {form.type === "bundle" && (() => {
                  // Runde 125.2: Bundle-Inhalte dürfen aus JEDEM Artikel-Typ
                  // bestehen (Fertigware, Verpackung UND Rohstoff) — nur
                  // andere Bundles ausschließen, um Endlosschleifen bei der
                  // Verfügbarkeitsberechnung zu vermeiden. Der aktuell
                  // bearbeitete Artikel wird ebenfalls ausgeblendet.
                  const bundleableItems = items.filter((i) =>
                    (i.type || "").toLowerCase() !== "bundle"
                    && i.id !== editing);
                  const comps = form.bundle_components || [];
                  let componentsCost = 0;
                  let minAvailability = null;
                  let hasMissingStock = false;
                  comps.forEach((c) => {
                    const it = items.find((x) => x.id === c.item_id);
                    if (!it) return;
                    const q = Number(c.quantity) || 0;
                    const pp = Number(it.purchase_price) || 0;
                    componentsCost += pp * q;
                    const stock = Number(it.stock) || 0;
                    const perBundle = q > 0 ? Math.floor(stock / q) : 0;
                    if (q > 0) {
                      if (minAvailability == null || perBundle < minAvailability) minAvailability = perBundle;
                      if (stock <= 0) hasMissingStock = true;
                    }
                  });
                  const salePrice = Number(form.price) || 0;
                  const margin = salePrice - componentsCost;
                  const marginPct = salePrice > 0 ? (margin / salePrice) * 100 : 0;
                  return (
                    <div className="col-span-2 border-t pt-4 mt-2" data-testid="bundle-components-editor">
                      <div className="flex items-center justify-between mb-2">
                        <div className="overline text-[var(--pop-pink)]">Bundle-Inhalt</div>
                        <Button type="button" variant="outline" size="sm" className="rounded-full"
                                data-testid="btn-add-bundle-component"
                                onClick={() => setForm({ ...form, bundle_components: [...comps, { item_id: "", quantity: 1 }] })}>
                          <Plus size={12} className="mr-1" /> Artikel hinzufügen
                        </Button>
                      </div>
                      <div className="text-xs text-[var(--text-muted)] mb-3">
                        Wähle beliebige Artikel (Fertigwaren, Verpackungen, Rohstoffe) aus, die zu diesem Bundle gehören.
                        Beim Verkauf werden die enthaltenen Artikel automatisch vom Lagerbestand abgezogen.
                        {bundleableItems.length === 0 && (
                          <span className="block text-amber-700 mt-1">
                            ⚠ Keine Artikel im System — lege zuerst mindestens einen Rohstoff / Verpackung / Fertigware an.
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        {comps.length === 0 && (
                          <div className="text-xs italic text-[var(--text-muted)] p-3 rounded-lg bg-neutral-50 border border-dashed">
                            Noch keine Bundle-Inhalte. Füge z.B. „3× Popcorn Salz 100g" + „2× Popcorn Karamell 100g" hinzu.
                          </div>
                        )}
                        {comps.map((c, idx) => {
                          const it = items.find((x) => x.id === c.item_id);
                          const stock = Number(it?.stock) || 0;
                          const q = Number(c.quantity) || 0;
                          const perBundle = q > 0 ? Math.floor(stock / q) : 0;
                          return (
                            <div key={idx} className="grid grid-cols-[minmax(240px,1fr)_100px_140px_36px] gap-2 items-center"
                                 data-testid={`bundle-comp-${idx}`}>
                              <ItemCombobox
                                items={bundleableItems}
                                value={c.item_id}
                                onChange={(v) => { const arr = [...comps]; arr[idx].item_id = v; setForm({ ...form, bundle_components: arr }); }}
                                placeholder="Artikel wählen…"
                                testId={`select-bundle-comp-${idx}`}
                              />
                              <Input type="number" step="any" min="0" placeholder="Anzahl"
                                     data-testid={`input-bundle-comp-qty-${idx}`}
                                     value={c.quantity}
                                     onChange={(e) => { const arr = [...comps]; arr[idx].quantity = normalizeDecimal(e.target.value); setForm({ ...form, bundle_components: arr }); }} />
                              <div className="text-xs font-mono text-right" data-testid={`bundle-comp-stock-${idx}`}>
                                {it ? (
                                  <>
                                    <div className={stock <= 0 ? "text-red-600 font-bold" : "text-[var(--text-muted)]"}>
                                      Bestand: {stock} {it.unit || ""}
                                    </div>
                                    {q > 0 && (
                                      <div className={perBundle <= 0 ? "text-red-600 font-bold" : "text-green-700"}>
                                        = {perBundle}× Bundle
                                      </div>
                                    )}
                                  </>
                                ) : <span className="text-[var(--text-muted)] italic">—</span>}
                              </div>
                              <button type="button" className="text-red-500 hover:text-red-700"
                                      data-testid={`btn-remove-bundle-comp-${idx}`}
                                      onClick={() => setForm({ ...form, bundle_components: comps.filter((_, i) => i !== idx) })}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      {comps.length > 0 && (
                        <div className="mt-3 rounded-xl bg-[var(--pop-pink)]/5 border border-[var(--pop-pink)]/30 p-3 grid grid-cols-4 gap-2 text-xs"
                             data-testid="bundle-margin-preview">
                          <div>
                            <div className="overline text-[10px]">Warenwert (EK)</div>
                            <div className="font-mono font-bold text-lg">{componentsCost.toFixed(2)} €</div>
                          </div>
                          <div>
                            <div className="overline text-[10px]">Bundle-VK</div>
                            <div className="font-mono font-bold text-lg">{salePrice > 0 ? `${salePrice.toFixed(2)} €` : "—"}</div>
                          </div>
                          <div>
                            <div className="overline text-[10px]">Marge</div>
                            <div className={`font-mono font-bold text-lg ${margin >= 0 ? "text-green-700" : "text-red-700"}`}
                                 data-testid="bundle-margin">
                              {salePrice > 0 ? `${margin.toFixed(2)} € (${marginPct.toFixed(0)} %)` : "—"}
                            </div>
                          </div>
                          <div>
                            <div className="overline text-[10px]">Verfügbar</div>
                            <div className={`font-mono font-bold text-lg ${hasMissingStock || (minAvailability != null && minAvailability <= 0) ? "text-red-600" : "text-green-700"}`}
                                 data-testid="bundle-availability">
                              {minAvailability != null ? `${minAvailability}× Bundle` : "—"}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Bundle-Editor: 3 feste Verkaufsgrößen */}
                {form.type === "finished" && (
                  <div className="col-span-2 border-t pt-4 mt-2" data-testid="bundle-editor">
                    <div className="overline mb-2 text-[var(--pop-pink)]">Bundle-Verkaufsgrößen</div>
                    <div className="text-xs text-[var(--text-muted)] mb-3">Aktivierte Bundles erscheinen als eigene Verkaufseinheit in Lieferscheinen. Preis wird automatisch aus Einzelpreis × Einheiten (13 / 40 / 520) vorbefüllt und ist überschreibbar.</div>
                    <div className="space-y-2">
                      {["13er", "40er", "palette"].map((code) => {
                        const b = form.bundles?.[code] || {};
                        const label = code === "13er" ? "13er Display" : code === "40er" ? "40er Display" : "Palette";
                        return (
                          <div key={code} className="grid grid-cols-[36px_1fr_1fr_1fr] gap-2 items-center">
                            <input type="checkbox" checked={!!b.enabled}
                                   data-testid={`bundle-${code}-enabled`}
                                   onChange={(e) => {
                                     const enabled = e.target.checked; const nb = { ...b, enabled };
                                     if (enabled && (b.price == null || b.price === "")) nb.price = autoBundlePrice(form.price, code);
                                     setForm({ ...form, bundles: { ...form.bundles, [code]: nb } });
                                   }}
                                   className="w-4 h-4 accent-[var(--pop-pink)]" />
                            <div className="text-sm font-semibold">{label}</div>
                            <Input placeholder="Preis (€)" type="number" step="0.01"
                                   data-testid={`bundle-${code}-price`}
                                   disabled={!b.enabled}
                                   value={b.price ?? ""}
                                   onChange={(e) => setForm({ ...form, bundles: { ...form.bundles, [code]: { ...b, price: e.target.value } } })} />
                            <Input placeholder="EAN"
                                   data-testid={`bundle-${code}-ean`}
                                   disabled={!b.enabled}
                                   value={b.ean ?? ""}
                                   onChange={(e) => setForm({ ...form, bundles: { ...form.bundles, [code]: { ...b, ean: e.target.value } } })}
                                   className="font-mono text-xs" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {editing && priceHistory.length > 0 && (
                  <div className="col-span-2 border-t pt-4 mt-2" data-testid="price-history">
                    <div className="overline mb-2">Preis-Historie ({priceHistory.length})</div>
                    <ul className="space-y-1 max-h-40 overflow-y-auto pr-2">
                      {priceHistory.map((h, i) => (
                        <li key={i} className="flex items-center justify-between text-xs py-1 border-b border-neutral-100 last:border-0">
                          <div className="font-mono text-[var(--text-muted)]">{h.changed_at?.slice(0, 10)}</div>
                          <div>
                            <span className="text-[var(--text-muted)] mr-2">{h.previous_price != null ? Number(h.previous_price).toFixed(2) + " €" : "—"}</span>
                            <span className="text-[var(--text-muted)]">→</span>
                            <span className="ml-2 font-semibold">{Number(h.price).toFixed(2)} €</span>
                          </div>
                          <div className="text-[var(--text-muted)] truncate ml-2 max-w-[100px]">{h.user_name || "—"}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <DialogFooter className="col-span-2 mt-2">
                  <Button type="submit" data-testid="btn-save-item" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Speichern</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input placeholder="Suche SKU, Name, Barcode…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-xl" data-testid="input-search-items" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48 rounded-xl" data-testid="select-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Typen</SelectItem>
            <SelectItem value="raw">Rohstoffe</SelectItem>
            <SelectItem value="packaging">Verpackungen</SelectItem>
            <SelectItem value="finished">Fertigwaren</SelectItem>
            <SelectItem value="bundle">Bundles / Sets</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selected.size > 0 && (
        <div className="sticky top-2 z-30 mb-4 rounded-2xl bg-black text-white p-4 flex items-center justify-between gap-4 shadow-2xl shadow-black/40"
             data-testid="items-bulk-toolbar">
          <div className="text-sm">
            <span className="font-bold">{selected.size}</span> ausgewählt
          </div>
          <div className="flex gap-2">
            <Button onClick={clearSelection} variant="outline" size="sm"
                    className="rounded-full bg-transparent text-white border-white/40 hover:bg-white/10"
                    data-testid="btn-clear-item-selection">Abwählen</Button>
            <Button onClick={openBulkEdit} size="sm"
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
                    data-testid="btn-bulk-edit-items">
              <Wand2 size={14} className="mr-1" /> {selected.size} bearbeiten
            </Button>
            <Button onClick={bulkDelete} size="sm"
                    className="rounded-full bg-red-600 hover:bg-red-700 text-white font-bold"
                    data-testid="btn-bulk-delete-items">
              <Trash2 size={14} className="mr-1" /> {selected.size} löschen
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={toggleAll}
                  data-testid="checkbox-select-all-items"
                  aria-label="Alle auswählen"
                />
              </TableHead>
              <TableHead className="w-14 overline">Bild</TableHead>
              <TableHead className="overline">SKU</TableHead>
              <TableHead className="overline">Name</TableHead>
              <TableHead className="overline">Typ</TableHead>
              <TableHead className="overline">Einheit</TableHead>
              <TableHead className="overline">Min-Bestand</TableHead>
              <TableHead className="overline">Preis</TableHead>
              <TableHead className="overline text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} data-testid={`item-row-${it.sku}`}>
                <TableCell>
                  <Checkbox
                    checked={selected.has(it.id)}
                    onCheckedChange={() => toggleOne(it.id)}
                    data-testid={`checkbox-item-${it.sku}`}
                    aria-label={`${it.sku} auswählen`}
                  />
                </TableCell>
                <TableCell>
                  <Link to={`/artikel/${it.id}`} data-testid={`item-thumb-${it.sku}`}>
                    <div className="w-10 h-10 rounded-lg border border-[var(--border)] bg-[var(--pop-cream)]/30 flex items-center justify-center overflow-hidden hover:ring-2 hover:ring-[var(--pop-pink)]/40 transition-all">
                      {it.image_data
                        ? <img src={it.image_data} alt={it.name} className="w-full h-full object-cover" />
                        : <ImageIcon size={14} className="text-[var(--text-muted)]" />}
                    </div>
                  </Link>
                </TableCell>
                <TableCell className="font-mono text-sm">
                  <Link to={`/artikel/${it.id}`} className="hover:underline text-[var(--pop-pink)] font-semibold" data-testid={`item-link-${it.sku}`}>
                    {it.sku}
                  </Link>
                </TableCell>
                <TableCell className="font-medium">
                  <Link to={`/artikel/${it.id}`} className="hover:underline">{it.name}</Link>
                </TableCell>
                <TableCell><TypeBadge type={it.type} /></TableCell>
                <TableCell className="font-mono text-xs">{it.unit}</TableCell>
                <TableCell className="font-mono text-xs">
                  {it.min_stock}
                  {it.type === "raw" && it.container_size && it.container_label && (
                    <div className="text-[10px] text-orange-700 mt-0.5" data-testid={`item-gebinde-${it.sku}`}>
                      Gebinde: {it.container_size} {it.container_unit || "kg"} / {it.container_label}
                      {it.stock != null && (
                        <div className="text-[10px] text-[var(--text-muted)]">
                          Bestand: {Number(it.stock).toLocaleString("de-DE")} {it.unit}
                          {" ("}
                          {Math.floor(Number(it.stock) / containerToBase(it.container_size, it.container_unit || "kg"))} {it.container_label} à {it.container_size} {it.container_unit || "kg"}
                          {")"}
                        </div>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">{it.price ? `${it.price.toFixed(2)} €` : "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button data-testid={`btn-batches-${it.sku}`}
                          onClick={async () => {
                            setBatchesFor(it);
                            setBatchesLoading(true);
                            try {
                              const { data } = await api.get("/batches", { params: { item_id: it.id, page_size: 200 } });
                              setBatchesRows(data);
                            } catch { setBatchesRows([]); }
                            finally { setBatchesLoading(false); }
                          }}
                          className="text-[var(--pop-pink)] hover:underline mr-3 inline-flex items-center gap-1 text-xs font-semibold"
                          title="Aktive Chargen anzeigen">
                    <Layers size={14} /> Chargen
                  </button>
                  <button data-testid={`btn-shelf-${it.sku}`}
                          onClick={() => openPdf(`/items/${it.id}/shelf-label.pdf`).catch(() => toast.error("Regal-Etikett konnte nicht geöffnet werden"))}
                          className="text-[var(--text-muted)] hover:text-[var(--pop-caramel)] mr-3 inline-flex items-center gap-1 text-xs font-semibold"
                          title="Regal-Etikett drucken">
                    <Printer size={14} /> Regal
                  </button>
                  <button data-testid={`btn-duplicate-${it.sku}`}
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/items/${it.id}/duplicate`);
                              toast.success(`Dupliziert als ${data.sku}`);
                              load();
                              openEdit(data);  // öffnet Dialog zur Nachbearbeitung
                            } catch (err) { toast.error(err.response?.data?.detail || "Fehler beim Duplizieren"); }
                          }}
                          title="Artikel duplizieren"
                          className="text-[var(--text-muted)] hover:text-[var(--pop-pink)] mr-3">
                    <Copy size={16} />
                  </button>
                  <button data-testid={`btn-barcode-${it.sku}`}
                          onClick={() => openPdf(`/items/${it.id}/barcode.pdf`).catch(() => toast.error("Barcode konnte nicht geöffnet werden"))}
                          className="text-[var(--text-muted)] hover:text-[var(--pop-pink)] mr-3 inline-flex items-center gap-1 text-xs font-semibold"
                          title="Barcode-Etikett drucken (Code128 + QR)">
                    <Barcode size={14} /> Barcode
                  </button>
                  <button data-testid={`btn-edit-${it.sku}`} onClick={() => openEdit(it)} className="text-[var(--text-muted)] hover:text-[var(--text)]"><Pencil size={16} /></button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-[var(--text-muted)]">Noch keine Artikel angelegt.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} onPageSizeChange={(n)=>{setPage(1);setPageSize(n);}} storageKey="items" testIdPrefix="items-pager" />
      </div>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-bulk-edit-items">
          <DialogHeader><DialogTitle className="font-display text-2xl">Bulk-Bearbeitung</DialogTitle></DialogHeader>
          <div className="text-sm text-[var(--text-muted)] -mt-1 mb-4">
            Nur ausgefüllte Felder werden auf die {selected.size} ausgewählten Artikel angewendet.
          </div>
          <form onSubmit={saveBulkEdit} className="grid grid-cols-2 gap-3">
            <div>
              <Label>Preis (€ netto)</Label>
              <Input type="number" step="0.01" data-testid="bulk-price"
                     value={bulk.price} onChange={(e) => setBulk({ ...bulk, price: e.target.value })} />
            </div>
            <div>
              <Label>MwSt-Satz</Label>
              <Select value={String(bulk.vat_rate)} onValueChange={(v) => setBulk({ ...bulk, vat_rate: v === "keep" ? "" : v })}>
                <SelectTrigger data-testid="bulk-vat"><SelectValue placeholder="Unverändert" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Unverändert</SelectItem>
                  <SelectItem value="0.07">7 %</SelectItem>
                  <SelectItem value="0.19">19 %</SelectItem>
                  <SelectItem value="0">0 %</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Min-Bestand</Label>
              <Input type="number" data-testid="bulk-min-stock"
                     value={bulk.min_stock} onChange={(e) => setBulk({ ...bulk, min_stock: e.target.value })} />
            </div>
            <div>
              <Label>MHD-Laufzeit (Tage)</Label>
              <Input type="number" data-testid="bulk-shelflife"
                     value={bulk.default_shelf_life_days}
                     onChange={(e) => setBulk({ ...bulk, default_shelf_life_days: e.target.value })} />
            </div>
            <div>
              <Label>Lagerplatz / Regal</Label>
              <Input placeholder="z.B. A-01-03" data-testid="bulk-location"
                     value={bulk.warehouse_location}
                     onChange={(e) => setBulk({ ...bulk, warehouse_location: e.target.value })} className="font-mono" />
            </div>
            <div>
              <Label>Einheit</Label>
              <Select value={bulk.unit || "keep"} onValueChange={(v) => setBulk({ ...bulk, unit: v === "keep" ? "" : v })}>
                <SelectTrigger data-testid="bulk-unit"><SelectValue placeholder="Unverändert" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Unverändert</SelectItem>
                  <SelectItem value="stk">Stück</SelectItem>
                  <SelectItem value="kg">Kilogramm</SelectItem>
                  <SelectItem value="g">Gramm</SelectItem>
                  <SelectItem value="l">Liter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="col-span-2 gap-2">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setBulkOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={bulkSaving} data-testid="btn-save-bulk-items"
                      className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                {bulkSaving ? "Speichere…" : `${selected.size} Artikel aktualisieren`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Aktive Chargen Drill-Down */}
      <Dialog open={!!batchesFor} onOpenChange={(o) => !o && (setBatchesFor(null), setBatchesRows([]))}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Layers size={22} className="text-[var(--pop-pink)]" />
              Chargen – {batchesFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-[var(--text-muted)] mb-3 font-mono">
            {batchesFor?.sku} · {batchesFor?.unit} · sortiert nach FEFO (nächstes MHD zuerst)
          </div>
          <div className="border border-[var(--border)] rounded-xl overflow-hidden max-h-[60vh] overflow-y-auto">
            {batchesLoading && <div className="p-6 text-center text-[var(--text-muted)]">Lade…</div>}
            {!batchesLoading && (
              <Table className="table-zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead className="overline">Charge-Nr.</TableHead>
                    <TableHead className="overline">MHD</TableHead>
                    <TableHead className="overline text-right">Restmenge</TableHead>
                    <TableHead className="overline">Regal</TableHead>
                    <TableHead className="overline">QC</TableHead>
                    <TableHead className="overline text-right">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batchesRows.filter((b) => (b.current_stock ?? 0) > 0).map((b) => {
                    const days = b.days_to_mhd;
                    const isExpired = days != null && days < 0;
                    const isWarning = days != null && days >= 0 && days <= 30;
                    return (
                      <TableRow key={b.id} data-testid={`item-batch-row-${b.charge_number}`}
                                className={isExpired ? "bg-red-50" : ""}>
                        <TableCell className="font-mono text-xs">{b.charge_number}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {b.mhd}
                          {days != null && (
                            <span className={`ml-2 text-[10px] font-bold ${isExpired ? "text-red-600" : isWarning ? "text-orange-600" : "text-[var(--text-muted)]"}`}>
                              {isExpired ? `abgelaufen (−${Math.abs(days)}T)` : `noch ${days}T`}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-right text-sm font-semibold">{b.current_stock} {b.item_unit}</TableCell>
                        <TableCell className="font-mono text-xs">{b.location || "—"}</TableCell>
                        <TableCell>
                          <span className={`pop-pill text-[10px] ${b.quality_status === "passed" ? "bg-green-100 text-green-800" : b.quality_status === "failed" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}>
                            {b.quality_status || "pending"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {isExpired
                            ? <span className="pop-pill bg-red-600 text-white font-bold">abgelaufen</span>
                            : isWarning
                              ? <span className="pop-pill bg-orange-100 text-orange-800 font-bold">bald ablaufend</span>
                              : <span className="text-xs text-[var(--text-muted)]">ok</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {batchesRows.filter((b) => (b.current_stock ?? 0) > 0).length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-[var(--text-muted)]">Keine aktiven Chargen für diesen Artikel.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        entityLabel="Artikel"
        previewEndpoint="/items/import/preview"
        commitEndpoint="/items/import/commit"
        templateUrl={`${API_BASE}/items/import/template.xlsx`}
        previewKey="rows"
        onDone={() => load()}
        renderPreviewHeader={() => (
          <>
            <th className="text-left p-2">Zeile</th>
            <th className="text-left p-2">SKU</th>
            <th className="text-left p-2">Name</th>
            <th className="text-left p-2">Gruppe</th>
            <th className="text-right p-2">Preis</th>
            <th className="text-left p-2">Aktion</th>
            <th className="text-left p-2">Hinweis</th>
          </>
        )}
        renderPreviewRow={(row, i) => {
          const badgeCls = row.action === "create"
            ? "bg-green-100 text-green-700"
            : row.action === "update" ? "bg-blue-100 text-blue-700"
            : "bg-red-100 text-red-700";
          return (
            <tr key={i} className="border-t border-[var(--border)]" data-testid={`import-item-row-${i}`}>
              <td className="p-2 text-[var(--text-muted)] font-mono">{row._row}</td>
              <td className="p-2 font-mono">{row.sku}</td>
              <td className="p-2">{row.name}</td>
              <td className="p-2">{row.product_group}</td>
              <td className="p-2 text-right font-mono">{row.price != null ? row.price.toFixed(2) : "—"}</td>
              <td className="p-2"><span className={`pop-pill text-[10px] font-bold ${badgeCls}`}>{row.action}</span></td>
              <td className="p-2 text-red-700 text-[11px]">{row.errors?.join("; ") || (row.existing_name ? `→ ${row.existing_name}` : "")}</td>
            </tr>
          );
        }}
      />
    </div>
  );
}
